import { useEffect, useRef, useState } from 'react';
import { useLogto } from '@logto/react';
import { getConfig } from '@/config';
import { setIdentityAssertionProvider, setStaffTokenProvider } from '@/apps/apis/claudeCodeDashboard/token';

export interface StaffMember {
  email: string | null;
  name: string | null;
}

export type StaffSessionStatus = 'loading' | 'anonymous' | 'staff' | 'refused';

/** A token with no scope claim granted nothing; reading it as "everything" would open every screen. */
const readScopes = (claim: unknown): string[] =>
  typeof claim === 'string' && claim.length > 0 ? claim.split(' ').filter(Boolean) : [];

export interface StaffSession {
  status: StaffSessionStatus;
  member: StaffMember | null;
  /** What the organization token actually granted — the console gates on these, never on role names. */
  scopes: string[];
}

/**
 * Turns a Logto sign-in into the credential the internal API reads: mint a token scoped to the
 * staff organization and hand it to the API client. Membership of that organization is what makes
 * someone staff, so an account outside it is refused here rather than left to fail per request.
 *
 * Mount once, above the router.
 */
/** How long a busy identity provider is waited for before the session is judged without it. */
const BUSY_WAIT_LIMIT_MS = 8_000;

export function useStaffSession(): StaffSession {
  const logto = useLogto();
  const { isAuthenticated, isLoading } = logto;
  const [session, setSession] = useState<StaffSession>({ status: 'loading', member: null, scopes: [] });

  // The SDK returns fresh function identities every render; depending on them would restart the
  // build each time and cancel the one in flight, so it would never finish.
  const client = useRef(logto);
  client.current = logto;

  // One mint per sign-in. The loading flag flipping mid-build is enough to start a second.
  const build = useRef<'idle' | 'running' | 'settled'>('idle');

  // The SDK's sign-in raises its busy flag and leaves it raised, expecting the page to leave for the provider. When
  // that hand-off does not happen the flag never drops, so after a while the flag is no longer believed.
  const [stoppedWaiting, setStoppedWaiting] = useState(false);
  useEffect(() => {
    if (!isLoading) {
      setStoppedWaiting(false);
      return;
    }
    const timer = setTimeout(() => setStoppedWaiting(true), BUSY_WAIT_LIMIT_MS);
    return () => clearTimeout(timer);
  }, [isLoading]);
  const waiting = isLoading && !stoppedWaiting;

  useEffect(() => {
    // Logto reports "not signed in" until it finishes rehydrating; acting on that would sign a
    // signed-in visitor out on every reload.
    if (waiting) {
      return;
    }

    if (!isAuthenticated) {
      build.current = 'idle';
      setStaffTokenProvider(null);
      setIdentityAssertionProvider(null);
      setSession({ status: 'anonymous', member: null, scopes: [] });
      return;
    }

    if (build.current !== 'idle') {
      return;
    }
    build.current = 'running';

    void (async () => {
      const { getOrganizationToken, getOrganizationTokenClaims, getIdTokenClaims } = client.current;
      const organizationId = getConfig().logtoStaffOrganizationId;
      try {
        const [token, organizationClaims, claims] = await Promise.all([
          getOrganizationToken(organizationId),
          getOrganizationTokenClaims(organizationId),
          getIdTokenClaims(),
        ]);
        if (!token) {
          throw new Error('the provider minted no organization token');
        }
        // The SDK owns the token from here: it caches one and refreshes it before expiry, so the
        // client asks for a fresh one per request rather than holding this first copy.
        setStaffTokenProvider(() => client.current.getOrganizationToken(organizationId));
        // Sent alongside the organization token; the API re-verifies it and reads the address from
        // it, which is the only thing that tells these screens whose rows to show.
        setIdentityAssertionProvider(() => client.current.getIdToken());
        setSession({
          status: 'staff',
          member: { email: claims?.email ?? null, name: claims?.name ?? null },
          scopes: readScopes(organizationClaims?.scope),
        });
        build.current = 'settled';
      } catch {
        // Signed in somewhere, but not into this console. Holding a half-built session would let
        // the shell render staff screens for an account the API will refuse.
        setStaffTokenProvider(null);
        setIdentityAssertionProvider(null);
        setSession({ status: 'refused', member: null, scopes: [] });
        build.current = 'settled';
      }
    })();
  }, [isAuthenticated, waiting]);

  return session;
}
